import React, { useState, useCallback, useEffect, useRef } from 'react';

//  Layout components 
import LibrarySidebar from '../components/LibrarySidebar';
import Toolbar from '../components/Toolbar';
import DragWrapper from '../components/DragWrapper';

//  Canvas renderer (dispatches to every canvas-component) 
import CanvasRenderer from '../components/CanvasRenderer';

//  Every canvas-component explicitly imported 
import PageHeader from '../components/page_header/PageHeader';
import SectionCard from '../components/canvas-components/SectionCard';
import FlexContainer from '../components/canvas-components/FlexContainer';
import GridContainer from '../components/canvas-components/GridContainer';
import { FreeDiv, Columns, Divider } from '../components/canvas-components/LayoutMisc';
import {
  StatCard, DataTable, BarChart, LineChart, PieChart, Badge,
} from '../components/canvas-components/DataComponents';
import {
  InputField, Dropdown, DatePicker, TextArea, FileUpload, SearchBar,
} from '../components/canvas-components/FormComponents';
import {
  Button, TextBlock, AlertBox, EmptyState, ImageBlock, RichHtml,
} from '../components/canvas-components/ContentComponents';

//  Property panels 
import PropsPanel from '../components/panels/PropsPanel';
import NonePanel from '../components/panels/NonePanel';
import {
  Toggle, ColorRow, TextInput, NumberInput, Select,
  SH, HR, Row2, Row3, BorderSection, PaddingSection,
  FBStyleSection, AddButton, CellSlot,
} from '../components/panels/PanelPrimitives';
import { PageHeaderPanel } from '../components/page_header/PageHeaderPanel';
import {
  SectionPanel, FlexPanel, GridPanel, FreeDivPanel,
  ColumnsPanel, DividerPanel, StatCardPanel, DataTablePanel,
  BarChartPanel, LineChartPanel, PieChartPanel, BadgePanel,
  InputFieldPanel, DropdownPanel, DatePickerPanel, TextAreaPanel,
  FileUploadPanel, SearchBarPanel, ButtonPanel, TextBlockPanel,
  AlertBoxPanel, EmptyStatePanel, ImageBlockPanel, RichHtmlPanel,
} from '../components/panels/EditorPanels';

//  Utilities & constants 
import { makeDefault, deepClone, uid } from '../components/helpers';
import { C, LIBRARY, libFind, SNAP_SIZE } from '../components/constants';

// 
// COMPONENT REGISTRY
// Maps every type string  its canvas component + panel component + group.
// Use this if you need to programmatically look up what renders for a type.
// 
export const COMPONENT_REGISTRY = {
  // Layout
  page_header:    { canvas: PageHeader,     panel: PageHeaderPanel,  group: 'Layout' },
  section:        { canvas: SectionCard,    panel: SectionPanel,     group: 'Layout' },
  flex_row:       { canvas: FlexContainer,  panel: FlexPanel,        group: 'Layout' },
  flex_col:       { canvas: FlexContainer,  panel: FlexPanel,        group: 'Layout' },
  grid_container: { canvas: GridContainer,  panel: GridPanel,        group: 'Layout' },
  free_div:       { canvas: FreeDiv,        panel: FreeDivPanel,     group: 'Layout' },
  columns:        { canvas: Columns,        panel: ColumnsPanel,     group: 'Layout' },
  divider:        { canvas: Divider,        panel: DividerPanel,     group: 'Layout' },
  // Data Display
  stat_card:      { canvas: StatCard,       panel: StatCardPanel,    group: 'Data Display' },
  data_table:     { canvas: DataTable,      panel: DataTablePanel,   group: 'Data Display' },
  chart_bar:      { canvas: BarChart,       panel: BarChartPanel,    group: 'Data Display' },
  chart_line:     { canvas: LineChart,      panel: LineChartPanel,   group: 'Data Display' },
  chart_pie:      { canvas: PieChart,       panel: PieChartPanel,    group: 'Data Display' },
  badge:          { canvas: Badge,          panel: BadgePanel,       group: 'Data Display' },
  // Forms
  fb_input:       { canvas: InputField,     panel: InputFieldPanel,  group: 'Forms' },
  fb_dropdown:    { canvas: Dropdown,       panel: DropdownPanel,    group: 'Forms' },
  fb_date:        { canvas: DatePicker,     panel: DatePickerPanel,  group: 'Forms' },
  fb_textarea:    { canvas: TextArea,       panel: TextAreaPanel,    group: 'Forms' },
  fb_file:        { canvas: FileUpload,     panel: FileUploadPanel,  group: 'Forms' },
  search_bar:     { canvas: SearchBar,      panel: SearchBarPanel,   group: 'Forms' },
  // Actions
  btn_primary:    { canvas: Button,         panel: ButtonPanel,      group: 'Actions' },
  btn_secondary:  { canvas: Button,         panel: ButtonPanel,      group: 'Actions' },
  btn_danger:     { canvas: Button,         panel: ButtonPanel,      group: 'Actions' },
  // Content
  text_block:     { canvas: TextBlock,      panel: TextBlockPanel,   group: 'Content' },
  alert_box:      { canvas: AlertBox,       panel: AlertBoxPanel,    group: 'Content' },
  empty_state:    { canvas: EmptyState,     panel: EmptyStatePanel,  group: 'Content' },
  image_block:    { canvas: ImageBlock,     panel: ImageBlockPanel,  group: 'Content' },
  rich_html:      { canvas: RichHtml,       panel: RichHtmlPanel,    group: 'Content' },
};

// 
// LOCAL STORAGE HELPERS
// 
const STORAGE_KEY = 'screen_builder_autosave';

function saveToStorage(screen, comps) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ screen, comps, savedAt: Date.now() }));
    return true;
  } catch { return false; }
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function doExportJSON(screen, comps) {
  const data = JSON.stringify({ screen, comps, exportedAt: new Date().toISOString() }, null, 2);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([data], { type: 'application/json' }));
  a.download = `${screen.name.replace(/\s+/g, '-')}.json`;
  a.click();
}

// 
// STATUS BAR
// Shows total count, per-group counts, selected comp info, zoom, snap
// 
function StatusBar({ comps, selComp, zoom, snapOn }) {
  const counts = {};
  comps.forEach((c) => {
    const g = COMPONENT_REGISTRY[c.type]?.group || 'Other';
    counts[g] = (counts[g] || 0) + 1;
  });

  return (
    <div style={{
      height: 28, background: C.surface, borderTop: `1px solid ${C.border}`,
      display: 'flex', alignItems: 'center', padding: '0 14px',
      gap: 16, flexShrink: 0, fontSize: 11, color: C.muted, userSelect: 'none',
    }}>
      <span style={{ color: C.sub, fontWeight: 500 }}>
        {comps.length} component{comps.length !== 1 ? 's' : ''}
      </span>
      {Object.entries(counts).map(([g, n]) => (
        <span key={g}><span style={{ color: C.muted }}>{g}: </span><span style={{ color: C.sub }}>{n}</span></span>
      ))}
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 14, alignItems: 'center' }}>
        {selComp && (
          <span style={{ color: C.sub }}>
            {libFind(selComp.type).icon} {libFind(selComp.type).label}
            {' '}
            <span style={{ fontFamily: 'monospace', color: C.muted, fontSize: 10 }}>
              x:{selComp.x} y:{selComp.y} w:{selComp.w}
            </span>
          </span>
        )}
        <span style={{ color: snapOn ? C.accent : C.muted }}>{snapOn ? '' : ''} {SNAP_SIZE}px</span>
        <span>{Math.round(zoom * 100)}%</span>
      </div>
    </div>
  );
}

// 
// MINI MAP
// Thumbnail overview of all components on the canvas
// 
function MiniMap({ comps, screenW, screenH, selId, onSelect }) {
  const SCALE = 0.11;
  const W = Math.round(screenW * SCALE);
  const H = Math.round(screenH * SCALE);

  return (
    <div style={{
      position: 'absolute', bottom: 12, right: 12,
      width: W + 2, height: H + 2,
      background: C.surface, border: `1px solid ${C.borderMd}`,
      borderRadius: 6, overflow: 'hidden',
      boxShadow: '0 2px 12px rgba(0,0,0,.12)',
      opacity: 0.9, zIndex: 10,
    }}>
      <svg width={W} height={H} style={{ display: 'block' }}>
        <rect width={W} height={H} fill={C.bg} />
        {comps.map((c) => {
          const x = Math.round((c.x || 0) * SCALE);
          const y = Math.round((c.y || 0) * SCALE);
          const w = Math.max(4, Math.round((c.w || 100) * SCALE));
          const h = Math.max(3, Math.round((c.h || 60) * SCALE));
          const isSel = c.id === selId;
          return (
            <rect key={c.id} x={x} y={y} width={w} height={h} rx={1}
              fill={isSel ? C.accent : C.accentMd}
              opacity={isSel ? 1 : 0.45}
              style={{ cursor: 'pointer' }}
              onClick={() => onSelect(c.id)}
            />
          );
        })}
      </svg>
    </div>
  );
}

// 
// CONTEXT MENU (right-click on a component)
// 
function ContextMenu({ x, y, comp, onDuplicate, onDelete, onBringFront, onSendBack, onClose }) {
  useEffect(() => {
    const h = () => onClose();
    window.addEventListener('mousedown', h);
    return () => window.removeEventListener('mousedown', h);
  }, [onClose]);

  const item = (label, action, danger = false, shortcut = '') => (
    <div
      onClick={(e) => { e.stopPropagation(); action(); onClose(); }}
      style={{
        padding: '7px 14px', cursor: 'pointer', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center',
        color: danger ? C.red : C.text, gap: 24,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span>{label}</span>
      {shortcut && <span style={{ fontSize: 10, color: C.muted }}>{shortcut}</span>}
    </div>
  );

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', left: x, top: y, zIndex: 9999,
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,.15)',
        padding: '4px 0', minWidth: 190, fontSize: 13,
      }}
    >
      {comp && (
        <div style={{
          padding: '6px 14px 8px', borderBottom: `1px solid ${C.border}`,
          fontSize: 11, color: C.muted,
        }}>
          {libFind(comp.type).icon}  {libFind(comp.type).label}
          <span style={{ fontFamily: 'monospace', marginLeft: 6, fontSize: 10 }}>{comp.id}</span>
        </div>
      )}
      {item('  Duplicate', onDuplicate, false, 'D')}
      {item('  Bring to front', onBringFront, false, ']')}
      {item('  Send to back', onSendBack, false, '[')}
      <div style={{ height: 1, background: C.border, margin: '3px 0' }} />
      {item('🗑  Delete', onDelete, true, 'Del')}
    </div>
  );
}

// 
// TOAST
// 
function Toast({ message, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2200); return () => clearTimeout(t); }, [onDone]);
  return (
    <div style={{
      position: 'fixed', bottom: 44, left: '50%', transform: 'translateX(-50%)',
      background: C.text, color: '#fff', borderRadius: 8, padding: '9px 20px',
      fontSize: 13, fontWeight: 500, boxShadow: '0 4px 16px rgba(0,0,0,.2)',
      zIndex: 9999, pointerEvents: 'none', whiteSpace: 'nowrap',
    }}>
      {message}
    </div>
  );
}

// 
// TINY BUTTON HELPER
// 
function TinyBtn({ onClick, color, children, style: extraStyle }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 10px', fontSize: 11, fontWeight: 500,
        border: `1px solid ${color}28`, borderRadius: 5,
        background: `${color}14`, color, cursor: 'pointer',
        fontFamily: 'inherit', transition: 'background .1s',
        ...extraStyle,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = `${color}28`; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = `${color}14`; }}
    >
      {children}
    </button>
  );
}

// 
// MAIN SCREEN BUILDER
// 
export default function ScreenBuilder() {

  //  Core state 
  const [comps, setComps]         = useState([]);
  const [history, setHistory]     = useState([]);          // undo stack
  const [future, setFuture]       = useState([]);          // redo stack
  const [selId, setSelId]         = useState(null);
  const [selectedChildId, setSelectedChildId] = useState(null); // selected child inside a container
  const [zoom, setZoomVal]        = useState(0.75);
  const [snapOn, setSnapOn]       = useState(true);
  const [previewMode, setPreview] = useState(false);
  const [showMiniMap, setMiniMap] = useState(true);
  const [screen, setScreen]       = useState({ name: 'Untitled Screen', w: 1280, h: 900, bg: '#F7F8FA' });

  //  UI overlays 
  const [contextMenu, setCtxMenu] = useState(null);   // { x, y, compId }
  const [toast, setToast]         = useState(null);
  const [isDirty, setDirty]       = useState(false);

  const boardRef    = useRef(null);
  const fileRef     = useRef(null);

  // 
  // AUTO-RESTORE on mount
  // 
  useEffect(() => {
    const saved = loadFromStorage();
    if (saved?.comps?.length > 0) {
      const mins = Math.round((Date.now() - (saved.savedAt || 0)) / 60000);
      if (window.confirm(
        `Restore autosaved session from ${mins} min ago? (${saved.comps.length} components)`
      )) {
        setComps(saved.comps);
        if (saved.screen) setScreen(saved.screen);
        showToast('Session restored');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //  Auto-save on every change 
  useEffect(() => {
    if (comps.length > 0) { saveToStorage(screen, comps); setDirty(true); }
  }, [comps, screen]);

  // 
  // HISTORY  (undo / redo)
  // 
  const pushHistory = useCallback((snapshot) => {
    setHistory((h) => [...h.slice(-49), deepClone(snapshot)]);
    setFuture([]);
  }, []);

  const undo = useCallback(() => {
    setHistory((h) => {
      if (!h.length) return h;
      const stack = [...h];
      const prev  = stack.pop();
      setFuture((f) => [deepClone(comps), ...f.slice(0, 19)]);
      setComps(prev);
      setSelId(null);
      return stack;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comps]);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (!f.length) return f;
      const stack = [...f];
      const next  = stack.shift();
      setHistory((h) => [...h, deepClone(comps)]);
      setComps(next);
      return stack;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comps]);

  // 
  // CRUD
  // 
  const addComp = useCallback((type) => {
    setComps((prev) => {
      pushHistory(prev);
      const c   = makeDefault(type);
      const off = (prev.length % 10) * 18;
      c.x = 40 + off;
      c.y = 40 + off;
      setSelId(c.id);
      return [...prev, c];
    });
  }, [pushHistory]);

  // lightweight patch  no history push (used during drag/resize)
  const updateComp = useCallback((id, patch) => {
    setComps((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }, []);

  // full replacement from PropsPanel  pushes history
  const replaceComp = useCallback((updated) => {
    setComps((prev) => {
      pushHistory(prev);
      return prev.map((c) => (c.id === updated.id ? updated : c));
    });
  }, [pushHistory]);

  const deleteComp = useCallback((id) => {
    setComps((prev) => {
      pushHistory(prev);
      setSelId((s) => (s === id ? null : s));
      return prev.filter((c) => c.id !== id);
    });
  }, [pushHistory]);

  const duplicateComp = useCallback((id) => {
    setComps((prev) => {
      const src = prev.find((x) => x.id === id);
      if (!src) return prev;
      pushHistory(prev);
      const n = deepClone(src);
      n.id = uid(); n.x = src.x + 24; n.y = src.y + 24;
      setSelId(n.id);
      return [...prev, n];
    });
  }, [pushHistory]);

  const bringToFront = useCallback((id) => {
    setComps((prev) => {
      const i = prev.findIndex((c) => c.id === id);
      if (i < 0) return prev;
      pushHistory(prev);
      const next = [...prev];
      next.push(next.splice(i, 1)[0]);
      return next;
    });
  }, [pushHistory]);

  const sendToBack = useCallback((id) => {
    setComps((prev) => {
      const i = prev.findIndex((c) => c.id === id);
      if (i < 0) return prev;
      pushHistory(prev);
      const next = [...prev];
      next.unshift(next.splice(i, 1)[0]);
      return next;
    });
  }, [pushHistory]);

  const clearAll = useCallback(() => {
    if (!window.confirm(`Clear all ${comps.length} components?`)) return;
    pushHistory(comps);
    setComps([]); setSelId(null);
    showToast('Canvas cleared');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comps, pushHistory]);

  // 
  // ZOOM  (also Ctrl+scroll)
  // 
  const handleZoom = useCallback((d) => {
    setZoomVal((z) => Math.max(0.2, Math.min(2, +(z + d).toFixed(2))));
  }, []);

  useEffect(() => {
    const el = document.getElementById('canvas-scroll');
    if (!el) return;
    const handler = (e) => {
      if (e.ctrlKey || e.metaKey) { e.preventDefault(); handleZoom(e.deltaY < 0 ? 0.05 : -0.05); }
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [handleZoom]);

  // 
  // KEYBOARD SHORTCUTS
  // 
  useEffect(() => {
    const handler = (e) => {
      const editing = ['input', 'textarea', 'select'].includes(
        document.activeElement.tagName.toLowerCase()
      );
      if (editing) return;

      if ((e.key === 'Delete' || e.key === 'Backspace') && selId) { e.preventDefault(); deleteComp(selId); }
      if (e.key === 'Escape')                                       { setSelId(null); setCtxMenu(null); }
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) { e.preventDefault(); undo(); }
      if ((e.metaKey || e.ctrlKey) &&  e.shiftKey && (e.key === 'z' || e.key === 'Z')) { e.preventDefault(); redo(); }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'd' || e.key === 'D') && selId)       { e.preventDefault(); duplicateComp(selId); }
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S'))               { e.preventDefault(); handleSave(); }
      if (e.key === ']' && selId) { e.preventDefault(); bringToFront(selId); }
      if (e.key === '[' && selId) { e.preventDefault(); sendToBack(selId); }

      // Arrow-key nudge
      const NUDGE = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
      if (NUDGE[e.key] && selId) {
        e.preventDefault();
        const step = e.shiftKey ? 20 : 1;
        const [dx, dy] = NUDGE[e.key];
        setComps((prev) => prev.map((c) =>
          c.id === selId
            ? { ...c, x: Math.max(0, c.x + dx * step), y: Math.max(0, c.y + dy * step) }
            : c
        ));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId, deleteComp, duplicateComp, undo, redo, bringToFront, sendToBack]);

  // 
  // SAVE / EXPORT / IMPORT
  // 
  const showToast = (msg) => setToast(msg);

  const handleSave = useCallback(() => {
    const ok = saveToStorage(screen, comps);
    showToast(ok ? ' Saved to browser storage' : ' Save failed');
    setDirty(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, comps]);

  const handleExport = useCallback(() => {
    doExportJSON(screen, comps);
    showToast('JSON exported');
  }, [screen, comps]);

  const handleImport = () => fileRef.current?.click();

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!Array.isArray(data.comps)) throw new Error('bad format');
        pushHistory(comps);
        setComps(data.comps);
        if (data.screen) setScreen(data.screen);
        setSelId(null);
        showToast(`Imported ${data.comps.length} components`);
      } catch { showToast(' Import failed  invalid JSON'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // 
  // CANVAS EVENTS
  // 
  const onBoardClick      = (e) => { if (e.target === boardRef.current) { setSelId(null); setCtxMenu(null); } };
  const onBoardRightClick = (e) => { e.preventDefault(); setCtxMenu(null); };
  const onCompRightClick  = useCallback((e, id) => {
    e.preventDefault(); e.stopPropagation();
    setSelId(id);
    setCtxMenu({ x: e.clientX, y: e.clientY, compId: id });
  }, []);

  // 
  // DERIVED
  // 
  const selComp    = comps.find((c) => c.id === selId) || null;
  const ctxComp    = contextMenu ? comps.find((c) => c.id === contextMenu.compId) : null;
  const isWide     = selComp && ['data_table','grid_container','flex_row','flex_col','columns','page_header'].includes(selComp.type);
  const totalTypes = LIBRARY.reduce((a, g) => a + g.items.length, 0);

  // 
  // RENDER
  // 
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '125vh', overflow: 'hidden',
      fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      fontSize: 13, color: C.text, background: C.bg,
    }}>
      {/* Hidden file input for JSON import */}
      <input ref={fileRef} type="file" accept=".json" style={{ display: 'none' }} onChange={onFileChange} />

      {/* 
          MAIN ROW  (sidebar | canvas area | props panel)
       */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/*  LEFT SIDEBAR 
            LibrarySidebar:
             Branding header
             Search box (filters LIBRARY items)
             Accordion groups: Layout / Data Display / Forms / Actions / Content
              Each item card: icon  label  desc   onClick calls addComp(type)
             Layers panel at bottom (all comps reversed)
              Layer row: icon  label  [×]   onClick calls setSelId
         */}
        <LibrarySidebar
          comps={comps}
          selId={selId}
          onAdd={addComp}
          onSelect={setSelId}
          onDelete={deleteComp}
        />

        {/*  CENTRE COLUMN  */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>

          {/*  TOOLBAR 
              Toolbar:
               Editable screen name
               Zoom:   %  +  1:1  Fit
               Undo (disabled when history empty)
               Snap toggle
               Preview toggle
               Export button  handleExport
           */}
          <Toolbar
            screenName={screen.name}
            onNameChange={(name) => setScreen((s) => ({ ...s, name }))}
            zoom={zoom}
            onZoom={handleZoom}
            onZoomReset={() => setZoomVal(1)}
            onZoomFit={() => setZoomVal(0.75)}
            canUndo={history.length > 0}
            onUndo={undo}
            snapOn={snapOn}
            onToggleSnap={() => setSnapOn((v) => !v)}
            previewMode={previewMode}
            onTogglePreview={() => setPreview((v) => !v)}
            onExport={handleExport}
          />

          {/*  SUB-TOOLBAR (save / import / minimap / clear)  */}
          <div style={{
            height: 34, background: C.bg, borderBottom: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', padding: '0 12px', gap: 6,
            flexShrink: 0,
          }}>
            <TinyBtn onClick={handleSave} color={C.green}>
              {isDirty ? ' ' : ''}Save
            </TinyBtn>
            <TinyBtn onClick={handleImport} color={C.accent}> Import JSON</TinyBtn>
            <TinyBtn onClick={() => setMiniMap((v) => !v)} color={showMiniMap ? C.purple : C.sub}>
              {showMiniMap ? ' Map on' : ' Map off'}
            </TinyBtn>
            {comps.length > 0 && (
              <TinyBtn onClick={clearAll} color={C.red} style={{ marginLeft: 'auto' }}> Clear all</TinyBtn>
            )}
            <span style={{ color: C.muted, fontSize: 11, marginLeft: comps.length > 0 ? 0 : 'auto' }}>
              {totalTypes} component types  {Object.keys(COMPONENT_REGISTRY).length} registered
            </span>
          </div>

          {/*  CANVAS SCROLL 
              Outer div scrolls; inner sizer matches board × zoom.
              Board is position:absolute, transform:scale(zoom), origin top-left.
              Ctrl+wheel zooms via event listener above.
           */}
          <div
            id="canvas-scroll"
            style={{ flex: 1, overflow: 'auto', background: '#C8CBD2', padding: 40, position: 'relative' }}
          >
            <div style={{ width: screen.w * zoom, height: screen.h * zoom, position: 'relative', flexShrink: 0 }}>
              <div
                ref={boardRef}
                onClick={onBoardClick}
                onContextMenu={onBoardRightClick}
                style={{
                  width: screen.w, minHeight: screen.h,
                  background: screen.bg,
                  position: 'absolute', top: 0, left: 0,
                  transform: `scale(${zoom})`, transformOrigin: 'top left',
                  boxShadow: '0 8px 40px rgba(0,0,0,.14), 0 1px 4px rgba(0,0,0,.08)',
                  borderRadius: 4, boxSizing: 'border-box',
                }}
              >
                {/* Empty hint */}
                {comps.length === 0 && !previewMode && (
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 12, pointerEvents: 'none',
                  }}>
                    <div style={{ fontSize: 56, opacity: 0.06 }}></div>
                    <div style={{ fontSize: 16, color: C.muted, fontWeight: 500 }}>Canvas is empty</div>
                    <div style={{ fontSize: 13, color: C.muted, opacity: 0.6 }}>
                       Click any component in the left panel to add it
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, opacity: 0.45, marginTop: 4 }}>
                      Right-click a component for context menu  S to save  Ctrl+scroll to zoom
                    </div>
                  </div>
                )}

                {/* 
                    COMPONENT RENDERING LOOP
                    Every comp in state is rendered here.
                    Preview mode  bare div (no drag handles)
                    Edit mode     DragWrapper (drag + SE/E resize handles)
                    Inside both   CanvasRenderer dispatches to:
                      Layout:  PageHeader, SectionCard, FlexContainer,
                               GridContainer, FreeDiv, Columns, Divider
                      Data:    StatCard, DataTable, BarChart, LineChart,
                               PieChart, Badge
                      Forms:   InputField, Dropdown, DatePicker, TextArea,
                               FileUpload, SearchBar
                      Actions: Button (shared for btn_primary/secondary/danger)
                      Content: TextBlock, AlertBox, EmptyState,
                               ImageBlock, RichHtml
                 */}
                {comps.map((comp) => {
                  const w = typeof comp.w === 'number' ? comp.w : 300;
                  const h = typeof comp.h === 'number' ? comp.h : undefined;

                  if (previewMode) {
                    return (
                      <div key={comp.id} style={{ position: 'absolute', left: comp.x, top: comp.y, width: w, height: h }}>
                        <CanvasRenderer comp={comp} allComps={comps} onChildSelect={setSelectedChildId} selectedChildId={selectedChildId} />
                      </div>
                    );
                  }

                  return (
                    <DragWrapper
                      key={comp.id}
                      comp={{ ...comp, _zoom: zoom }}
                      selected={selId === comp.id}
                      snapOn={snapOn}
                      onSelect={() => { setSelId(comp.id); setCtxMenu(null); }}
                      onUpdate={(patch) => updateComp(comp.id, patch)}
                      onContextMenu={(e) => onCompRightClick(e, comp.id)}
                    >
                      <CanvasRenderer comp={comp} allComps={comps} onChildSelect={setSelectedChildId} selectedChildId={selectedChildId} />
                    </DragWrapper>
                  );
                })}
              </div>
            </div>

            {/*  MINI MAP 
                MiniMap:
                 Small SVG thumbnail of entire canvas
                 Each comp drawn as a colored rect (accent if selected)
                 Click a rect to select that component
             */}
            {showMiniMap && comps.length > 0 && (
              <MiniMap
                comps={comps}
                screenW={screen.w}
                screenH={screen.h}
                selId={selId}
                onSelect={setSelId}
              />
            )}
          </div>
        </div>

        {/*  RIGHT PANEL 
            When a component is selected  PropsPanel:
               Header: icon  label  id   [ dup]  [🗑 del]
               Position & Size grid: x  y  w  h
               Tab bar: content | style (style only for fb_ types)
               Content tab  EditorPanels dispatcher  one of:
                  PageHeaderPanel, SectionPanel, FlexPanel, GridPanel,
                  FreeDivPanel, ColumnsPanel, DividerPanel,
                  StatCardPanel, DataTablePanel, BarChartPanel,
                  LineChartPanel, PieChartPanel, BadgePanel,
                  InputFieldPanel, DropdownPanel, DatePickerPanel,
                  TextAreaPanel, FileUploadPanel, SearchBarPanel,
                  ButtonPanel, TextBlockPanel, AlertBoxPanel,
                  EmptyStatePanel, ImageBlockPanel, RichHtmlPanel
               Style tab  FBStyleSection (label + input box full control)
              All panels use PanelPrimitives:
                Toggle, ColorRow, TextInput, NumberInput, Select,
                SH, HR, Row2, Row3, BorderSection, PaddingSection,
                AddButton, CellSlot

            When nothing is selected  NonePanel:
               Canvas width / height / background color
               Keyboard shortcuts reference
         */}
        <div style={{
          width: isWide ? 520 : 340,
          background: C.surface, borderLeft: `1px solid ${C.border}`,
          flexShrink: 0, display: 'flex', flexDirection: 'column',
          overflow: 'hidden', transition: 'width .15s',
        }}>
          {selComp ? (
            <PropsPanel
              comp={selComp}
              allComps={comps}
              onChange={replaceComp}
              onDelete={() => deleteComp(selComp.id)}
              onDuplicate={() => duplicateComp(selComp.id)}
              selectedChildId={selectedChildId}
              onChildSelect={setSelectedChildId}
            />
          ) : (
            <NonePanel
              screenW={screen.w}
              screenH={screen.h}
              screenBg={screen.bg}
              onScreenChange={(patch) => setScreen((s) => ({ ...s, ...patch }))}
            />
          )}
        </div>
      </div>

      {/* 
          STATUS BAR
          Shows: total  per-group counts  selected comp info  snap  zoom
       */}
      <StatusBar comps={comps} selComp={selComp} zoom={zoom} snapOn={snapOn} />

      {/* 
          CONTEXT MENU  (right-click on component)
          Options: Duplicate  Bring to front  Send to back  Delete
       */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          comp={ctxComp}
          onDuplicate={() => duplicateComp(contextMenu.compId)}
          onDelete={() => deleteComp(contextMenu.compId)}
          onBringFront={() => bringToFront(contextMenu.compId)}
          onSendBack={() => sendToBack(contextMenu.compId)}
          onClose={() => setCtxMenu(null)}
        />
      )}

      {/* 
          TOAST NOTIFICATION  (2 sec auto-dismiss)
       */}
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}
